/* Genel uç nokta sözleşmeleri.
   Aynı şemayı Worker doğrulama için, admin arayüzü form durumu için,
   üretici de tip güvenliği için kullanır — tek tanım, üç tüketici. */

import { z } from 'zod';
import { SERVICE_KEYS, localized, pageSeo, postCover, slug } from './content.ts';
import type { Localized, PageSeo } from './content.ts';
import { ORIGINAL_EXTS, WATERMARK_STYLES } from './media.ts';
import type { WatermarkStyle } from './media.ts';

export const lang = z.enum(['tr', 'en']);
export type Lang = z.infer<typeof lang>;

/* ── İletişim formu ─────────────────────────────────────────
   Alan adları index.html'deki `name` öznitelikleriyle birebir aynıdır.
   Eşikler main.js:730-734'teki istemci kurallarını yansıtır; istemci
   doğrulaması atlanabildiği için sunucu da aynı kuralı uygular.

   NOT: `type` listesi index.html'den alınmıştır. docs/BACKEND.md §2
   `digital` seçeneğini atlıyor — o belge eksik, HTML doğru kaynak. */
export const contactInput = z.object({
  name: z.string().trim().min(2, 'Lütfen adınızı ve soyadınızı yazın.').max(120),
  email: z.string().trim().toLowerCase().email('Geçerli bir e-posta adresi girin.').max(200),
  type: z.enum(SERVICE_KEYS).optional(),
  brief: z
    .string()
    .trim()
    .min(20, 'Projeyi en az birkaç cümleyle anlatın (20+ karakter).')
    .max(5000),
  lang: lang.default('tr'),
  page: z.string().max(300).optional(),
  /**
   * Bal küpü — ekranda görünmez, insanlar boş bırakır, botlar doldurur.
   *
   * Kasten `max(0)` DEĞİL: şema burada reddetseydi bot 400 alır ve alanın
   * bir tuzak olduğunu öğrenirdi. Doluluk denetimi uç noktada yapılır ve
   * bota 200 döndürülür — kalıbını değiştirmesi için sebebi olmasın.
   */
  website: z.string().max(2000).optional(),
  turnstileToken: z.string().min(1).optional()
});
export type ContactInput = z.infer<typeof contactInput>;

/* ── Rezervasyon ────────────────────────────────────────── */

export const bookingSlot = z.enum(['full', 'am', 'pm']);
export type BookingSlot = z.infer<typeof bookingSlot>;

export const bookingInput = z.object({
  serviceKey: z.enum(SERVICE_KEYS),
  date: z.string().date(),
  slot: bookingSlot,
  clientName: z.string().trim().min(2).max(120),
  clientEmail: z.string().trim().toLowerCase().email().max(200),
  clientPhone: z.string().trim().max(40).optional(),
  brief: z.string().trim().min(20).max(5000),
  lang: lang.default('tr'),
  /** Bal küpü — iletişim formundaki ile aynı kural (yukarıdaki nota bakın). */
  website: z.string().max(2000).optional(),
  turnstileToken: z.string().min(1).optional()
});
export type BookingInput = z.infer<typeof bookingInput>;

export const availabilityQuery = z.object({
  from: z.string().date(),
  to: z.string().date()
});

export const availabilityDay = z.object({
  date: z.string().date(),
  slots: z.array(z.object({ slot: bookingSlot, open: z.boolean() }))
});
export type AvailabilityDay = z.infer<typeof availabilityDay>;

/* ── İmzalı büyütme adresleri (§5.3 Katman 1) ───────────── */

export const mediaTokenInput = z.object({
  /** Açılan kare + komşuları; toplu istenir ki ok tuşları beklemesin. */
  ids: z.array(z.string().min(1)).min(1).max(8),
  /** Dar ekranlar daha küçük katmanı ister (§15 bellek riski). */
  variant: z.enum(['full', 'mobile']).default('full')
});

export const mediaTokenOutput = z.object({
  urls: z.record(z.string(), z.string().url()),
  expiresAt: z.number().int()
});
export type MediaTokenOutput = z.infer<typeof mediaTokenOutput>;

/* ── Ölçüm işareti ──────────────────────────────────────── */

export const trackInput = z.object({
  event: z.enum(['photo_view', 'photo_download', 'album_view', 'page_view']),
  entityType: z.enum(['photo', 'album', 'post', 'page']),
  entityId: z.string().max(200),
  lang: lang.default('tr')
});

/* ── Yükleme: imzalı PUT adresleri (§5.2 adım 6) ─────────── */

/** Tarayıcının R2'ye doğrudan yazdığı katmanlar. LQIP base64 olarak commit gövdesinde gider. */
export const UPLOAD_TIERS = ['browse', 'full', 'orig'] as const;
export type UploadTier = (typeof UPLOAD_TIERS)[number];

/**
 * Kare kimliği — R2 anahtarının içindeki tek değişken parça.
 *
 * Alfabe kasten dar: anahtar `browse/<id>.webp` biçiminde kuruluyor ve
 * `photos.id` sütunu varchar(64). Noktaya, eğik çizgiye ya da yüzde kaçışına
 * izin verilseydi çağıran taraf anahtarı kendi yazardı — `../` ile önekten
 * çıkıp kovanın başka bir yerine imza almak buna dahil.
 *
 * Asıl savunma yine de burası değil: imzalanan anahtarı **istemci hiç
 * vermiyor**, sunucu bu kimlikten kuruyor (`key.browse` / `key.full` /
 * `key.orig`). Bu şema o kurulumun girdisini temiz tutar, tek başına
 * durmaz — giz sızsa bile yazılabilecek yer bu iki katmandır.
 */
export const photoId = z
  .string()
  .regex(
    /^[a-z0-9][a-z0-9-]{0,63}$/,
    'Kare kimliği küçük harf, rakam ve tireden oluşmalı; en fazla 64 karakter.'
  );

export const uploadSignInput = z.object({
  photoId,
  /** `orig` katmanının uzantısı; türevlerin uzantısı katmandan gelir. */
  originalExt: z.enum(ORIGINAL_EXTS).default('jpg'),
  /**
   * Hangi katmanlar için adres isteniyor. Varsayılan üçü birden; yeniden
   * denemede yalnızca düşen katman istenebilsin diye ayrıştırılabilir.
   */
  tiers: z
    .array(z.enum(UPLOAD_TIERS))
    .min(1)
    .max(UPLOAD_TIERS.length)
    .default([...UPLOAD_TIERS])
});
export type UploadSignInput = z.infer<typeof uploadSignInput>;

export type SignedUpload = {
  tier: UploadTier;
  /** Sunucunun kurduğu anahtar. İstemci bunu değiştiremez, yalnızca görür. */
  key: string;
  bucket: string;
  url: string;
};

export type UploadSignOutput = {
  uploads: SignedUpload[];
  /** ISO 8601. Bu andan sonra adresler R2 tarafından reddedilir. */
  expiresAt: string;
};

/* ── Karenin veritabanına işlenmesi (plan §6, §8) ───────── */

/**
 * `POST /api/admin/photos` gövdesi — baytlar R2'ye kondu, sıra satırda.
 *
 * Yükleme iki adımdır ve ikisi bilerek ayrı: imzalı PUT baytları kovaya
 * koyar (tarayıcı → R2, Worker'dan geçmez), bu uç ise üstveriyi yazar.
 * Ayrılığın sebebi, birinin diğeri olmadan da anlamlı olabilmesi — yarıda
 * kalan bir yığında nesneler kovada durur ve ikinci çalıştırma yalnızca
 * eksik satırları tamamlar (packages/imaging/upload.ts'teki defter).
 *
 * Ölçüler istemciden geliyor ve bu bilinçli: genişlik, yükseklik ve oran
 * türevi **üreten** yerde biliniyor (tuval), sunucu baytları hiç görmüyor.
 * Sunucunun bunları yeniden ölçmesi, dosyayı Worker'dan geçirmek demekti —
 * yani mimarinin reddettiği şey. Zarar yüzeyi sınırlı: yanlış oran bozuk
 * bir ızgara hücresi verir, güvenlik sınırı değildir.
 */
export const photoCommitInput = z.object({
  id: photoId,
  width: z.number().int().positive().max(20_000),
  height: z.number().int().positive().max(20_000),
  /** `--r` CSS değişkeni; ızgara hücresinin en/boy oranı. */
  ratio: z.number().positive().max(10),
  originalExt: z.enum(ORIGINAL_EXTS).default('jpg'),
  bytesBrowse: z.number().int().nonnegative().optional(),
  bytesFull: z.number().int().nonnegative().optional(),
  bytesOriginal: z.number().int().nonnegative().optional(),
  /**
   * Bulanık zemin, base64 data URI. Üst sınır sayfayı korumak için: LQIP
   * her ızgara hücresinin HTML'ine gömülüyor, yani 40 karelik bir sayfada
   * kırk kez tekrar ediyor. 4 KB'lık bir LQIP o sayfaya 160 KB ekler.
   */
  lqip: z.string().max(4_000).optional(),
  /** Alt metin; TR zorunlu, EN yoksa TR'ye düşer. Erişilebilirlik + SEO. */
  alt: localized,
  /** EXIF çekim tarihi. ISO 8601; okunamadıysa hiç gönderilmez. */
  capturedAt: z.string().datetime().nullish(),
  camera: z.record(z.string(), z.union([z.string(), z.number()])).optional(),
  /**
   * Orijinalin SHA-256'sı. `photos_checksum_idx` UNIQUE olduğu için
   * tekrar yükleme veritabanı düzeyinde engelleniyor — uç bunu hata değil
   * **aynı kare** olarak yorumlar (§5.2 dedupe).
   */
  checksum: z
    .string()
    .regex(/^[0-9a-f]{64}$/, 'Sağlama toplamı 64 haneli onaltılık olmalı.')
    .optional()
});
export type PhotoCommitInput = z.infer<typeof photoCommitInput>;

/**
 * `GET /api/admin/photos` sorgusu — panelin kare seçicisi.
 *
 * İmleç sayfa numarası değil `createdAt`: numarayla sayfalarken araya yeni
 * bir kare girdiğinde bütün sayfalar kayar ve aynı kare iki kez görünür.
 * 2 GB'lık bir arşivde bu kaçınılmaz, çünkü yükleme ile gezinme aynı anda
 * oluyor.
 */
export const photoListQuery = z.object({
  /** Üst sınır panelin bir ekranda gösterebileceğinden fazlasını istemesin. */
  limit: z.coerce.number().int().positive().max(200).default(60),
  /** Bu andan **önce** yüklenenler; bir önceki sayfanın son karesinin anı. */
  before: z.string().datetime().optional(),
  /** Kimlikte alt dize araması — yönetici kareyi dosya adından tanıyor. */
  search: z.string().trim().max(64).optional()
});
export type PhotoListQuery = z.infer<typeof photoListQuery>;

export type PhotoCommitOutput = {
  id: string;
  /**
   * `true` ise bu kare zaten vardı ve satır **yazılmadı**.
   *
   * Yükleyici için hata değil bilgi: aynı çekimi ikinci kez atmak boşa
   * gitmesin diye (§5.2). Dönen `id` var olan karenin kimliğidir, gönderilen
   * değil — çağıran taraf albüme onu eklemeli.
   */
  duplicate: boolean;
  status: 'processing' | 'ready' | 'failed';
};

/* ── Yönetici girişi (plan §6, §11) ─────────────────────── */

/**
 * Şifre için asgari uzunluk.
 *
 * 12, uzunluğu karmaşıklık kuralına yeğ tutan bugünkü tavsiyeye uyuyor
 * (NIST SP 800-63B): dört sözcüklük bir parola, zorunlu noktalama işaretiyle
 * süslenmiş sekiz karakterden hem daha güçlü hem daha akılda kalıcıdır.
 * Üst sınır PBKDF2'nin değil, kötü niyetli uzun girdinin sınırı.
 */
export const MIN_ADMIN_PASSWORD_LENGTH = 12;

export const adminLoginInput = z.object({
  email: z.string().trim().toLowerCase().email('Geçerli bir e-posta adresi girin.').max(200),
  password: z.string().min(MIN_ADMIN_PASSWORD_LENGTH).max(200),
  /** TOTP açıksa altı hane; kapalıysa hiç gönderilmez (plan §11). */
  totp: z
    .string()
    .trim()
    .regex(/^\d{6}$/)
    .optional()
});
export type AdminLoginInput = z.infer<typeof adminLoginInput>;

export type AdminLoginOutput = {
  email: string;
  /** ISO 8601 — panel çerezin ne zaman öleceğini bilsin. */
  expiresAt: string;
};

/* ── Yönetici içerik uçları (plan §6, Kilometre taşı 8) ───

   Panelin dört içerik tablosu — albümler, arşiv ızgarası, günlük ve
   hizmetler — aynı iki şemayla sürülür: `…Input` yaratmak için, `…Patch`
   değiştirmek için.

   İkisinin farkı tek bir kuralda toplanıyor ve bütün uçlar ona uyuyor:
   **gövdede olmayan alana dokunulmaz, `null` yazılan alan temizlenir.**
   `.partial()` birinciyi, `.nullish()` ikincisini veriyor. Tek şema ile
   idare edip "boş string = sil" demek, düzeltilmek istenen bir başlığı
   silmenin en kolay yolu olurdu. */

/**
 * İçerik satırlarının kimliği.
 *
 * `photoId`'den tek farkı alt çizgiye izin vermesi: bu kimlikler `alb_`,
 * `wrk_`, `pst_`, `svc_` önekleriyle kuruluyor (aşağıdaki `contentId`).
 * Alfabe yine dar tutuluyor çünkü kimlik yol parçası olarak geçiyor
 * (`/api/admin/albums/:id`) — nokta, eğik çizgi ve yüzde kaçışı dışarıda.
 */
export const entityId = z
  .string()
  .regex(
    /^[a-z0-9][a-z0-9_-]{0,63}$/,
    'Kimlik küçük harf, rakam, tire ve alt çizgiden oluşmalı; en fazla 64 karakter.'
  );

/** Tohumun ve yönetici uçlarının ortak kimlik biçimi. */
export const ID_PREFIX = {
  album: 'alb',
  work: 'wrk',
  post: 'pst',
  service: 'svc',
  availability: 'avl'
} as const;

/**
 * Kimliği anahtarından kurar — `alb_deri-inci` gibi.
 *
 * Tohum (packages/db/src/seed.ts) ile yönetici uçları **aynı** işlevi
 * çağırır. Ayrı dursalardı panelden açılan bir albüm `album_…`, tohumdan
 * gelen `alb_…` olurdu; ikisi de çalışır ve fark ancak birinin diğerine
 * atıfta bulunduğu gün görünürdü.
 *
 * `varchar(64)` sınırı burada kesilir, veritabanında değil: kesme işini
 * Postgres'e bırakmak, uzun bir slug'ın sessizce başkasının kimliğine
 * çarpması demekti.
 */
export function contentId(prefix: (typeof ID_PREFIX)[keyof typeof ID_PREFIX], key: string): string {
  return `${prefix}_${key}`.slice(0, 64);
}

const year = z.number().int().min(1900).max(2200);

/**
 * Sıralı kare listesi — tekrarsız.
 *
 * Tekrar yasağı biçimsel bir titizlik değil: sıralama tek bir
 * `INSERT … ON CONFLICT DO UPDATE` ile yazılıyor ve Postgres aynı satırı
 * o ifade içinde iki kez güncellemeyi reddediyor ("cannot affect row a
 * second time"). Kural burada durursa panel anlaşılır bir alan hatası
 * görür; durmazsa 503.
 */
const photoIdList = z
  .array(photoId)
  .max(500)
  .refine((ids) => new Set(ids).size === ids.length, 'Aynı kare listede iki kez olamaz.');

/* ── Albümler ───────────────────────────────────────────── */

/**
 * Albüm yaratma gövdesi — kimlik **yok**, slug'dan türetiliyor.
 *
 * Panelin kimlik uydurması istenmiyor: slug zaten benzersiz (albums_slug_idx)
 * ve kimlik ondan kuruluyor, yani iki alanın çakışması tek bir çakışma.
 * İstemciye ikisini birden yazdırmak, birbirini tutmayan bir çiftin
 * veritabanına girmesine kapı açardı.
 */
export const albumInput = z.object({
  slug,
  title: localized,
  /** Başlığın `<em>` vurgulu hâli; `title` bunun düz karşılığıdır. */
  titleHtml: localized.nullish(),
  lede: localized,
  category: localized,
  year,
  coverPhotoId: photoId.nullish(),
  watermarkStyle: z.enum(WATERMARK_STYLES).default('corner'),
  /** Yayınlanmamış kampanya arama motorlarında görünmesin (plan §10). */
  noindex: z.boolean().default(false),
  published: z.boolean().default(false),
  sortIndex: z.number().int().nonnegative().default(0),
  seo: pageSeo.nullish(),
  /**
   * Albümün kareleri, gösterim sırasıyla. Yaratırken boş bırakılabilir —
   * karelerin yüklenmesi ayrı bir adım (§5.2) ve albüm o adımdan önce
   * açılabilmeli.
   */
  photoIds: photoIdList.default([])
});
export type AlbumInput = z.infer<typeof albumInput>;

/** Slug değiştirilebilir; kimlik değişmez — bir kez kurulur, kalıcıdır. */
export const albumPatch = albumInput.partial();
export type AlbumPatch = z.infer<typeof albumPatch>;

/**
 * `POST /api/admin/albums/:id/reorder` — hem üyelik hem sıra.
 *
 * Gövde albümün **tamamını** anlatır: listede olmayan kare albümden çıkar,
 * olan kare gönderilen sırayla yerleşir. Fark listesi (`add`/`remove`)
 * göndermek yerine böyle: sürükle-bırak arayüzü zaten nihai sırayı biliyor,
 * ve iki paralel düzenleme çakıştığında "son yazan kazanır" anlaşılır bir
 * sonuç verir — yarım uygulanmış bir fark listesi vermez.
 */
export const albumReorderInput = z.object({
  photoIds: photoIdList
});
export type AlbumReorderInput = z.infer<typeof albumReorderInput>;

/** Panelin albüm listesinde gösterdiği özet satır. */
export type AlbumSummary = {
  id: string;
  slug: string;
  title: Localized;
  category: Localized;
  year: number;
  coverPhotoId: string | null;
  published: boolean;
  noindex: boolean;
  sortIndex: number;
  watermarkStyle: WatermarkStyle;
  photoCount: number;
  updatedAt: string;
};

/** Tek albüm — özetin tamamı, artı sıralı kare listesi. */
export type AlbumDetail = AlbumSummary & {
  titleHtml: Localized | null;
  lede: Localized;
  seo: PageSeo | null;
  photoIds: string[];
};

/* ── Arşiv ızgarası (works.html) ────────────────────────── */

export const workInput = z.object({
  /** Kimlik burada istemciden gelir: kareyle bire bir değil, ızgara hücresi. */
  id: entityId,
  photoId,
  /** Doluysa kare albüm kapağıdır; büyütme yerine albüm sayfasına gider. */
  albumId: entityId.nullish(),
  category: localized,
  year,
  /** `--r` (en/boy) ve `--s` (ızgarada kapladığı görece alan). */
  ratio: z.number().positive().max(10),
  weight: z.number().positive().max(10).default(1),
  position: z.number().int().nonnegative().default(0),
  /**
   * Ana sayfa karuselindeki sıra; karuselde yer almayan kare için `null`.
   * `works_featured_idx` UNIQUE olduğu için iki kare aynı sırayı alamaz —
   * bu yüzden toplu sıralama tek ifadeyle yazılır (routes/adminWorks.ts).
   */
  /* Üst sınır `smallint`in tamamı: karusel sıraları aralıklı verilebilsin
     (10, 20, 30…) ki araya bir kare sokmak takas gerektirmesin. */
  featuredIndex: z.number().int().nonnegative().max(32_767).nullish(),
  published: z.boolean().default(true)
});
export type WorkInput = z.infer<typeof workInput>;

export const workPatch = workInput.partial().omit({ id: true });
export type WorkPatch = z.infer<typeof workPatch>;

/**
 * `POST /api/admin/works/reorder` — ızgaranın `position` sırası.
 *
 * Yalnızca `position`. Karusel sırası (`featuredIndex`) bu listede **yok**
 * ve olamaz: o sütunda tekil dizin var, Postgres tekil dizini satır satır
 * denetliyor, yani iki sırayı takas eden tek bir `UPDATE` düşüyor — ve
 * `neon-http` sürücüsünde işlem olmadığı için iki adıma bölmek de sarmalsız
 * kalırdı. Gerekçenin tamamı `routes/adminWorks.ts` başlığında.
 */
export const workReorderInput = z.object({
  ids: z
    .array(entityId)
    .max(500)
    .refine((ids) => new Set(ids).size === ids.length, 'Aynı hücre listede iki kez olamaz.')
});
export type WorkReorderInput = z.infer<typeof workReorderInput>;

/* ── Günlük ─────────────────────────────────────────────── */

export const postInput = z.object({
  slug,
  title: localized,
  /** Kırıntı yolu ve yazılar arası gezinme için kısa ad. */
  shortTitle: localized.nullish(),
  titleHtml: localized.nullish(),
  lede: localized.nullish(),
  excerpt: localized.nullish(),
  /**
   * Gövde HTML'i. Editör (TipTap, §8) temizlenmiş HTML üretir; yine de
   * üretici bunu şablona **ham** basıyor, yani buraya giren metin sayfaya
   * aynen çıkar. Uç bu yüzden kilidin arkasında ve yalnızca stüdyo yazar.
   */
  bodyHtml: localized.nullish(),
  category: localized,
  readMinutes: z.number().int().positive().max(120).default(5),
  coverPhotoId: photoId.nullish(),
  /** Stüdyo kendi karesini seçene kadarki dış kaynaklı kapak (§4). */
  cover: postCover.nullish(),
  seo: pageSeo.nullish(),
  /** `YYYY-AA-GG`; boşsa yazı henüz yayımlanmamış sayılır. */
  publishedAt: z.string().date().nullish()
});
export type PostInput = z.infer<typeof postInput>;

export const postPatch = postInput.partial();
export type PostPatch = z.infer<typeof postPatch>;

/* ── Hizmetler ──────────────────────────────────────────── */

/**
 * Hizmet kartları. `key` kapalı bir kümeden geliyor çünkü iletişim formunun
 * `type` alanı (index.html) ve rezervasyon gövdesi aynı listeyi kullanıyor —
 * serbest metin olsaydı panelden eklenen bir hizmet formda hiç görünmezdi.
 */
export const serviceInput = z.object({
  key: z.enum(SERVICE_KEYS),
  title: localized,
  desc: localized,
  meta: localized,
  durationDays: z.number().int().positive().max(60).nullish(),
  bookable: z.boolean().default(true),
  position: z.number().int().nonnegative().default(0)
});
export type ServiceInput = z.infer<typeof serviceInput>;

export const servicePatch = serviceInput.partial().omit({ key: true });
export type ServicePatch = z.infer<typeof servicePatch>;

/* ── Yayınlama (plan §7, §8) ────────────────────────────── */

export type PublishOutput = {
  /** Cloudflare Pages dağıtım kancasının kabul ettiği an. */
  queuedAt: string;
};

/* ── Ortak yanıt biçimi ─────────────────────────────────── */

export const errorResponse = z.object({
  error: z.string(),
  /** Alan bazlı hatalar; admin formları bunu doğrudan gösterir. */
  fields: z.record(z.string(), z.string()).optional()
});
export type ErrorResponse = z.infer<typeof errorResponse>;
